import { useEffect, useState, type ReactNode } from 'react'
import { Bookmark, Search, Tag, Users, X } from 'lucide-react'
import { cn } from '@/utils/cn'
import { GestorDashboardCriteriaMultiSelect } from '@/components/Gestores/GestorDashboardCriteriaMultiSelect'
import {
  normalizeCriteriaSelections,
  resolveCriteriaExecutors,
  resolveCriteriaWorkspaces,
} from '@/components/Gestores/gestorDashboardCriteriaUtils'
import {
  gestorDashboardFieldClass,
  gestorDashboardSectionTitleClass,
} from '@/components/Gestores/gestorDashboardFormStyles'
import type {
  GestorDashboardFilterCatalog,
  GestorDashboardFilterCriteria,
  GestorDashboardFilterPreset,
} from '@/components/Gestores/gestorDashboardPresetTypes'
import { hasAnyFilterCriteria } from '@/components/Gestores/gestorDashboardPresetTypes'

type Option = { value: string; label: string }

type Props = {
  open: boolean
  mode: 'create' | 'edit'
  initialName?: string
  initialCriteria?: GestorDashboardFilterCriteria
  currentCriteria: GestorDashboardFilterCriteria
  catalogs: GestorDashboardFilterCatalog | null
  workspaceOptions: Option[]
  executorOptions: Option[]
  preset?: GestorDashboardFilterPreset | null
  loading?: boolean
  onClose: () => void
  onSave: (input: { name: string; criteria: GestorDashboardFilterCriteria; isDefault: boolean }) => void
  onDelete?: () => void
}

function FieldLabel({ icon, children }: { icon?: ReactNode; children: ReactNode }) {
  return (
    <span className='flex items-center gap-1.5 text-xs font-medium text-text-muted'>
      {icon}
      {children}
    </span>
  )
}

export function GestorDashboardSaveFilterModal({
  open,
  mode,
  initialName = '',
  initialCriteria,
  currentCriteria,
  catalogs,
  workspaceOptions,
  executorOptions,
  preset,
  loading = false,
  onClose,
  onSave,
  onDelete,
}: Props) {
  const [name, setName] = useState(initialName)
  const [isDefault, setIsDefault] = useState(false)
  const [criteria, setCriteria] = useState<GestorDashboardFilterCriteria>(currentCriteria)

  useEffect(() => {
    if (!open) return
    setName(initialName || '')
    setIsDefault(preset?.isDefault ?? false)
    setCriteria(normalizeCriteriaSelections(initialCriteria ?? currentCriteria))
  }, [open, initialName, initialCriteria, currentCriteria, preset?.isDefault])

  if (!open) return null

  const canSave = name.trim().length > 0 && hasAnyFilterCriteria(criteria)
  const selectedWorkspaces = resolveCriteriaWorkspaces(criteria)
  const selectedExecutors = resolveCriteriaExecutors(criteria)

  return (
    <div className='fixed inset-0 z-50 flex items-center justify-center p-4'>
      <div className='absolute inset-0 bg-black/55 backdrop-blur-[2px]' aria-hidden onClick={onClose} />

      <div
        role='dialog'
        aria-label={mode === 'create' ? 'Salvar filtro' : 'Editar filtro'}
        className='relative z-10 flex max-h-[min(90vh,720px)] w-full max-w-2xl flex-col overflow-hidden rounded-2xl border border-[var(--glass-border)] bg-[var(--bg-card)] shadow-2xl'
      >
        <header className='flex shrink-0 items-start justify-between gap-3 border-b border-[var(--glass-border)] px-5 py-4'>
          <div className='min-w-0'>
            <div className='mb-1 flex items-center gap-2'>
              <div className='flex h-8 w-8 items-center justify-center rounded-lg bg-accent/10 text-accent'>
                <Bookmark size={16} aria-hidden />
              </div>
              <h3 className='text-base font-semibold text-text-primary'>
                {mode === 'create' ? 'Salvar filtro' : 'Editar filtro'}
              </h3>
            </div>
            <p className='text-xs text-text-muted'>
              Defina nome e critérios para reutilizar esta visão no dashboard.
            </p>
          </div>
          <button
            type='button'
            onClick={onClose}
            className='flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-text-muted transition-colors hover:bg-white/[0.06] hover:text-text-primary'
            aria-label='Fechar'
          >
            <X size={16} />
          </button>
        </header>

        <div className='min-h-0 flex-1 space-y-6 overflow-y-auto px-5 py-5 scrollbar-thin'>
          <section className='space-y-3'>
            <h4 className={gestorDashboardSectionTitleClass}>Identificação</h4>
            <label className='block space-y-1.5'>
              <FieldLabel>Nome do filtro</FieldLabel>
              <input
                value={name}
                onChange={(event) => setName(event.target.value)}
                placeholder='Ex.: Pauta Designers'
                className={gestorDashboardFieldClass}
              />
            </label>
          </section>

          <section className='space-y-3'>
            <h4 className={gestorDashboardSectionTitleClass}>Critérios</h4>

            <div className='grid gap-3 sm:grid-cols-2'>
              <label className='block space-y-1.5'>
                <FieldLabel icon={<Users size={13} aria-hidden />}>Squad</FieldLabel>
                <select
                  value={criteria.squadId ?? ''}
                  onChange={(event) => {
                    const squadId = event.target.value ? Number(event.target.value) : null
                    const squad = catalogs?.squads.find((item) => item.id === squadId)
                    setCriteria((prev) => ({
                      ...prev,
                      squadId,
                      squadName: squad?.label ?? null,
                    }))
                  }}
                  className={gestorDashboardFieldClass}
                >
                  <option value=''>Todos os squads</option>
                  {catalogs?.squads.map((squad) => (
                    <option key={squad.id} value={squad.id}>
                      {squad.label}
                    </option>
                  ))}
                </select>
              </label>

              <label className='block space-y-1.5'>
                <FieldLabel icon={<Tag size={13} aria-hidden />}>Tipo de tarefa</FieldLabel>
                <select
                  value={criteria.taskType ?? ''}
                  onChange={(event) => setCriteria((prev) => ({ ...prev, taskType: event.target.value || null }))}
                  className={gestorDashboardFieldClass}
                >
                  <option value=''>Todos os tipos</option>
                  {catalogs?.taskTypes.map((taskType) => (
                    <option key={taskType.id} value={taskType.label}>
                      {taskType.label}
                    </option>
                  ))}
                </select>
              </label>
            </div>

            <div className='grid gap-4 lg:grid-cols-2'>
              <GestorDashboardCriteriaMultiSelect
                label='Clientes'
                options={workspaceOptions}
                selected={selectedWorkspaces}
                emptyLabel='Todos os clientes'
                searchPlaceholder='Buscar cliente…'
                onChange={(workspaces) => setCriteria((prev) => ({ ...prev, workspaces, workspace: null }))}
              />

              <GestorDashboardCriteriaMultiSelect
                label='Executores'
                options={executorOptions}
                selected={selectedExecutors}
                emptyLabel='Todos os executores'
                searchPlaceholder='Buscar executor…'
                onChange={(executors) => setCriteria((prev) => ({ ...prev, executors, executor: null }))}
              />
            </div>

            <label className='block space-y-1.5'>
              <FieldLabel icon={<Search size={13} aria-hidden />}>Texto livre</FieldLabel>
              <input
                value={criteria.search ?? ''}
                onChange={(event) => setCriteria((prev) => ({ ...prev, search: event.target.value || null }))}
                placeholder='Tarefa, comentário, ticket…'
                className={gestorDashboardFieldClass}
              />
            </label>
          </section>

          <label className='flex cursor-pointer items-start gap-3 rounded-xl border border-[var(--glass-border)] bg-[var(--glass-bg)]/50 px-4 py-3 transition-colors hover:bg-white/[0.03]'>
            <input
              type='checkbox'
              checked={isDefault}
              onChange={(event) => setIsDefault(event.target.checked)}
              className='mt-0.5 h-4 w-4 rounded border-[var(--glass-border)] accent-accent'
            />
            <span className='min-w-0'>
              <span className='block text-sm font-medium text-text-primary'>Usar como filtro padrão</span>
              <span className='mt-0.5 block text-xs text-text-muted'>
                Aplica automaticamente ao abrir o dashboard Marketing.
              </span>
            </span>
          </label>
        </div>

        <footer className='flex shrink-0 flex-wrap items-center gap-2 border-t border-[var(--glass-border)] px-5 py-4'>
          {mode === 'edit' && onDelete && (
            <button
              type='button'
              disabled={loading}
              onClick={onDelete}
              className='rounded-lg border border-red-500/30 px-3 py-2 text-sm text-red-400 transition-colors hover:bg-red-500/10'
            >
              Excluir
            </button>
          )}
          <button
            type='button'
            onClick={onClose}
            className={cn('ml-auto rounded-lg border border-[var(--glass-border)] px-4 py-2 text-sm text-text-muted transition-colors hover:bg-white/[0.04] hover:text-text-primary')}
          >
            Cancelar
          </button>
          <button
            type='button'
            disabled={!canSave || loading}
            onClick={() => onSave({ name: name.trim(), criteria: normalizeCriteriaSelections(criteria), isDefault })}
            className='rounded-lg bg-accent px-4 py-2 text-sm font-semibold text-white transition-opacity hover:opacity-90 disabled:opacity-50'
          >
            Salvar filtro
          </button>
        </footer>
      </div>
    </div>
  )
}
